import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Download, FileCheck2, PackageOpen, TriangleAlert } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { fmtBytes } from '@/lib/documents';
import type { Program } from '@/lib/types';
import { fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Dialog } from '@/components/ui/dialog';
import { Alert, Badge, Card, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';

interface ExportRow {
  id: string;
  kind: 'PROGRAM' | 'AUDIT';
  state: 'running' | 'ready' | 'failed';
  requestedBy: string;
  createdAt: string;
  completedAt: string | null;
  sizeBytes: number | null;
  sha256: string | null;
  error: string | null;
}

const STATE = {
  running: { label: 'Building', tone: 'info' },
  ready: { label: 'Ready', tone: 'ok' },
  failed: { label: 'Failed', tone: 'bad' },
} as const;

interface PurgeState {
  purgedAt: string | null;
  programName: string;
  exportReady: { id: string; completedAt: string; sha256: string } | null;
  certificates: Array<{ id: string; purgedAt: string; requestedByName: string; pdfSha256: string }>;
}

/** Agreement §8: return (export) and destruction (purge with a certificate). */
export function DataTab({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/admin`;
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({
    queryKey: ['admin', program.id, 'exports'],
    queryFn: () => api.get<ExportRow[]>(`${base}/exports`),
    refetchInterval: (q) => (q.state.data?.some((x) => x.state === 'running') ? 2000 : false),
  });
  const start = useMutation({
    mutationFn: () => api.post(`${base}/exports`),
    onSuccess: () => {
      toast.ok('Building the export. It appears here when ready.');
      void qc.invalidateQueries({ queryKey: ['admin', program.id, 'exports'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const download = async (id: string) => {
    try {
      const { url } = await api.post<{ url: string }>(`${base}/exports/${id}/link`);
      const a = Object.assign(document.createElement('a'), { href: url, rel: 'noopener' });
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="max-w-2xl flex-1">
            <h2 className="text-sm font-semibold">Return package (Agreement §8)</h2>
            <p className="mt-1 text-sm text-muted">
              A zip of every document version {program.clientShortName} can see and every program record as JSON and
              CSV, with a manifest of counts and a SHA-256 for each file. BlackWolf's internal documents and the Work
              Record are not included. The link to download it lasts five minutes and is logged.
            </p>
          </div>
          <Button onClick={() => start.mutate()} loading={start.isPending}>
            <PackageOpen /> Build export
          </Button>
        </div>
      </Card>
      <Card className="p-0">
        <h2 className="border-b border-border px-4 py-2.5 text-sm font-semibold">Exports</h2>
        {!list.data ? (
          <Spinner className="h-24" />
        ) : !list.data.length ? (
          <p className="px-4 py-4 text-sm text-muted">No exports yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {list.data.map((x) => (
              <li key={x.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-[13px]">
                <span className="font-medium">{x.kind === 'PROGRAM' ? 'Return package' : 'Audit log CSV'}</span>
                <Badge tone={STATE[x.state].tone}>{STATE[x.state].label}</Badge>
                <span className="text-xs text-muted">
                  {fmtDateTime(x.createdAt)} by {x.requestedBy}
                  {x.sizeBytes ? ` · ${fmtBytes(x.sizeBytes)}` : ''}
                </span>
                {x.sha256 && (
                  <span className="font-mono text-[11px] text-muted" title="SHA-256 of the file">
                    {x.sha256.slice(0, 16)}
                  </span>
                )}
                {x.kind === 'PROGRAM' && x.state === 'ready' && (
                  <Button size="sm" variant="secondary" className="ml-auto" onClick={() => void download(x.id)}>
                    <Download /> Download
                  </Button>
                )}
                {x.error && <span className="w-full text-xs text-bad">{x.error}</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Purge program={program} />
    </div>
  );
}

function Purge({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/admin`;
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ confirmName: '', code: '', requestedByName: '' });
  const q = useQuery({ queryKey: ['admin', program.id, 'purge'], queryFn: () => api.get<PurgeState>(`${base}/purge`) });
  const purge = useMutation({
    mutationFn: () => api.post(`${base}/purge`, f),
    onSuccess: () => {
      toast.ok('Destroyed. The certificate is ready.');
      setOpen(false);
      void qc.invalidateQueries();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!q.data) return <Spinner className="h-24" />;
  const s = q.data;

  return (
    <Card className="border-bad/30 p-5">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-bad">
        <TriangleAlert className="size-4" /> Destroy {program.clientShortName}'s information (Agreement §8)
      </h2>
      {s.purgedAt ? (
        <p className="mt-2 text-sm">Destroyed on {fmtDateTime(s.purgedAt)}.</p>
      ) : (
        <>
          <p className="mt-1 max-w-3xl text-sm text-muted">
            Deletes every stored file and every record this program holds, ends client access and anonymizes client
            accounts. The audit trail stays with its content removed, as evidence. This cannot be undone. It needs a
            return package from the last 7 days, the program name typed exactly, and a fresh authenticator code.
          </p>
          {!s.exportReady ? (
            <Alert tone="warn" className="mt-3">
              Build the return package above first.
            </Alert>
          ) : (
            <Button variant="danger" className="mt-3" onClick={() => setOpen(true)}>
              <TriangleAlert /> Destroy and issue a certificate
            </Button>
          )}
        </>
      )}
      {s.certificates.map((c) => (
        <a
          key={c.id}
          href={`${base}/purge/certificates/${c.id}`}
          className="mt-3 flex items-center gap-2 text-sm text-brand-text hover:underline"
        >
          <FileCheck2 className="size-4" /> Certificate of destruction, {fmtDateTime(c.purgedAt)} (requested by{' '}
          {c.requestedByName})
        </a>
      ))}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Destroy this program's information?"
        description="Irreversible. Files and records are deleted now; the certificate records what was destroyed."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => purge.mutate()}
              loading={purge.isPending}
              disabled={f.confirmName !== s.programName || f.code.length < 6 || f.requestedByName.trim().length < 3}
            >
              Destroy
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field
            label={`Who at ${program.clientShortName} asked for destruction?`}
            htmlFor="pg-who"
            hint="Name and title, as it should read on the certificate."
          >
            <Input
              id="pg-who"
              value={f.requestedByName}
              onChange={(e) => setF({ ...f, requestedByName: e.target.value })}
            />
          </Field>
          <Field label={`Type the program name: ${s.programName}`} htmlFor="pg-name">
            <Input id="pg-name" value={f.confirmName} onChange={(e) => setF({ ...f, confirmName: e.target.value })} />
          </Field>
          <Field label="Authenticator code" htmlFor="pg-code">
            <Input
              id="pg-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={f.code}
              onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, '') })}
            />
          </Field>
        </div>
      </Dialog>
    </Card>
  );
}
